/* If you edit this file, please remove this header and clean up the resulting eslint errors.
*/
/* eslint-disable
  import/no-commonjs,
  import/no-extraneous-dependencies,
  import/no-named-as-default,
  react/prop-types
*/
import React from 'react';
import PropTypes from 'prop-types';
import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import { combineForms, actions } from 'react-redux-form';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { FormattedMessage } from 'react-intl';
import SimpleGrayLayout from 'common/components/simple-gray-layout';
import SignUpForm from 'hellosign/components/auth/sign-up-form';
import './sign-up-page.scss';
import StyleProvider from 'hello-ui/style';
import SplitSignupLayout from './split-signup-layout';
import CountriesDropdown from '../../../common/components/countries-dropdown';
import {
  EU_COUNTRIES,
  REST_OF_THE_WORLD,
  MARKETING_EMAIL_AUTO_OPT_IN_COUNTRIES,
  EUROPEAN_COUNTRIES_LIST,
  MARKETING_EMAIL_AUTO_OPT_IN_COUNTRIES_LIST,
} from '../pricing/constants';

const store = createStore(
  combineForms({
    signUp: {
      email: '',
      marketingOptIn: false,
      csrfToken: '',
      redirectUrl: '',
      pendingTsmGroupGuid: '',
    },
  }),
  composeWithDevTools(
    applyMiddleware(thunk),
  ),
);

/**
 * Top-level component for standalone Sign Up page.
 * Expects some props from the backend, such as CSRF token.
 */
class SignUpPage extends React.Component {
    static propTypes = {
      // CSRF Token - Sent from the server
      csrfToken: PropTypes.string.isRequired,

      siteCode: PropTypes.string.isRequired,

      // Email value - This is useful to provide for testing purposes
      emailAddress: PropTypes.string,

      redirectUrl: PropTypes.string,
      pendingTsmGroupGuid: PropTypes.string,

      // Used if this is a team invite signup (note: if provided, emailAddress is also required)
      teamInviteGuid: PropTypes.string,
      teamInviteSenderEmail: PropTypes.string,

      // 2 char country code that should be used when rendering this page
      initialCountryCode: PropTypes.string,

      // indicates where the sign up is coming from
      signupSource: PropTypes.string,

      // Used to only display the HelloSign sign up option for the Microsoft Office Integration
      isOfficeAddIn: PropTypes.bool,

      // Needed for Arkose
      arkoseSignUpPublicKey: PropTypes.string.isRequired,
      arkoseSignUpEnabled: PropTypes.bool.isRequired,
    };

    constructor(props) {
      super(props);
      this.onCountryCodeChange = this.onCountryCodeChange.bind(this);

      // This initial state is for Redux
      const initialState = {
        email: this.props.emailAddress,
        csrfToken: this.props.csrfToken,
        redirectUrl: this.props.redirectUrl,
        pendingTsmGroupGuid: this.props.pendingTsmGroupGuid,
        countryCode: this.props.initialCountryCode,
        marketingOptIn: false,
        arkoseSignUpPublicKey: this.props.arkoseSignUpPublicKey,
      };

      // This is a hack for OAuth flows and ensures that we redirect
      // back to the referrer when the user signs up. This fixes an issue
      // where new users would be taken out of the OAuth flow when attempting
      // to create a new account (DEV-2687)
      const ref = document.referrer;
      if (ref.indexOf('oauth') > 0) {
        initialState.redirectUrl = ref;
      }

      store.dispatch(actions.change('signUp', initialState));

      // This is for local state
      this.state = {
        countryCode: this.props.initialCountryCode,
      };
    }

    componentDidMount() {
      // Set defaults based on initial state of country code
      this.onCountryCodeChange(this.state.countryCode);
    }

    /**
     * Set opt-ins based on the user's country
     */
    onCountryCodeChange(countryCode) {
      if (countryCode !== undefined) {
        const implicitOptIn = MARKETING_EMAIL_AUTO_OPT_IN_COUNTRIES_LIST.includes(countryCode);
        store.dispatch(actions.change('signUp.marketingOptIn', implicitOptIn));
        store.dispatch(actions.change('signUp.countryCode', countryCode));
        this.setState({ countryCode });
      }
    }

    getLayout() {
      let layout = SplitSignupLayout;

      if (HfReactHelper.HfSites.isHelloSign(this.props.siteCode) || this.props.signupSource) {
        layout = SplitSignupLayout;
      } else {
        layout = SimpleGrayLayout;
      }
      return layout;
    }

    getSignInLink() {
      if (HfReactHelper.HfSites.isHelloSign(this.props.siteCode) || this.props.signupSource) {
        // When on HelloSign or if signupSource defined, this link is inside the sign up form
        return;
      }
      const {
        teamInviteGuid,
        logInUrl,
      } = this.props;
      const isInvite = !!teamInviteGuid;
      const signInLink = !isInvite
            && logInUrl
            && <div className="m-sign-up-page--sign-in-link">
              <p>
                <FormattedMessage
                  id=""
                  description="link text in sign up page, when clicked takes user sign in page"
                  defaultMessage="Already have an account? <a>Sign in ›</a>"
                  values={{
                    a: (...chunks) => <a href={logInUrl}>{chunks}</a>,
                  }} />
              </p>
            </div>;
      return signInLink;
    }

    render() {
      const LayoutTag = this.getLayout();

      let implicitlyOptedIn = '';
      if (EUROPEAN_COUNTRIES_LIST.includes(this.state.countryCode)) {
        implicitlyOptedIn = EU_COUNTRIES;
      } else if (MARKETING_EMAIL_AUTO_OPT_IN_COUNTRIES_LIST.includes(this.state.countryCode)) {
        implicitlyOptedIn = MARKETING_EMAIL_AUTO_OPT_IN_COUNTRIES;
      } else {
        implicitlyOptedIn = REST_OF_THE_WORLD;
      }

      return (
        <Provider store={store}>
          <StyleProvider>
            <div>
              <LayoutTag signupSource={this.props.signupSource}
                siteCode={this.props.siteCode}
              >
                <div className="m-sign-up-page--countries-dropdown">
                  <CountriesDropdown selectedCountryCode={this.state.countryCode}
                    onCountryCodeChange={this.onCountryCodeChange} />
                </div>
                <SignUpForm {...this.props} implicitlyOptedIn={implicitlyOptedIn} />
              </LayoutTag>
              {this.getSignInLink()}
            </div>
          </StyleProvider>
        </Provider>
      );
    }
}

module.exports = SignUpPage;
